import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Database, Plus, ChevronUp, ChevronDown, Pencil, Trash2, Check, X, Loader2, Lock } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import AreaOwnersCard from "./AreaOwnersCard";
import AdminGeoTidyUp from "./AdminGeoTidyUp";
import CsLeadCard from "./CsLeadCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDataManagement() {
  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [meta, setMeta] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newCode, setNewCode] = useState("");
  const [editing, setEditing] = useState(null);      // {id, label, code}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/lookups/types`).then((r) => {
      setTypes(r.data.types || []);
      if (r.data.types?.length) setActiveType(r.data.types[0].type);
    }).catch(() => toast.error("Failed to load data types"));
  }, []);

  const load = useCallback(async () => {
    if (!activeType || activeType === "geo") return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lookups/admin/${activeType}`);
      setItems(r.data.items || []);
      setMeta(r.data.meta || {});
    } catch (e) { toast.error("Failed to load items"); }
    finally { setLoading(false); }
  }, [activeType]);

  useEffect(() => { load(); }, [load]);

  const hasCode = !!meta.has_code;

  const add = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/lookups/admin/${activeType}`, { label: newLabel.trim(), code: newCode.trim() || null });
      setNewLabel(""); setNewCode("");
      toast.success("Added");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/lookups/admin/item/${editing.id}`, { label: editing.label, code: editing.code || null });
      setEditing(null);
      toast.success("Saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  const toggleActive = async (it) => {
    try {
      await axios.put(`${API}/lookups/admin/item/${it.id}`, { active: !it.active });
      await load();
    } catch (e) { toast.error("Failed to update"); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const ids = items.map((i) => i.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setItems((prev) => { const c = [...prev]; [c[idx], c[j]] = [c[j], c[idx]]; return c; });
    try { await axios.post(`${API}/lookups/admin/${activeType}/reorder`, { ids }); }
    catch (e) { toast.error("Failed to reorder"); load(); }
  };

  const del = async (it) => {
    if (it.in_use > 0) { toast.error(`"${it.label}" is used by ${it.in_use} record(s) — deactivate instead.`); return; }
    if (!window.confirm(`Delete "${it.label}"? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/lookups/admin/item/${it.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-data-management">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#EDE4FF] flex items-center justify-center">
          <Database className="w-5 h-5 text-[#7C3AED]" />
        </div>
        <div>
          <h2 className="heading text-xl text-[#1A1A1A] tracking-tight">Data Management</h2>
          <p className="text-sm text-[#59554D]">Manage the dropdown / combo-box options used across the app.</p>
        </div>
      </div>

      {/* Type tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {types.map((t) => (
          <button key={t.type} onClick={() => setActiveType(t.type)}
            className={`h-8 px-3 rounded-full text-[12px] font-semibold transition-colors ${activeType === t.type ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`}
            data-testid={`dm-type-${t.type}`}>{t.label}</button>
        ))}
        <button onClick={() => setActiveType("geo")}
          className={`h-8 px-3 rounded-full text-[12px] font-semibold transition-colors ${activeType === "geo" ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`}
          data-testid="dm-type-geo">Countries &amp; Currencies</button>
      </div>

      {activeType === "geo" ? <AdminGeoTidyUp /> : (
      <Card className="bg-white border-[#DAD5CA]">
        <CardContent className="p-0">
          {/* Add row */}
          <div className="flex items-end gap-2 p-4 border-b border-[#EDEAE3]">
            <div className="flex-1">
              <label className="text-[11px] text-[#59554D]">New option</label>
              <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder={`Add a ${meta.label || "option"}…`}
                className="h-9 border-[#DAD5CA]" data-testid="dm-new-label" />
            </div>
            {hasCode && (
              <div className="w-28">
                <label className="text-[11px] text-[#59554D]">Code</label>
                <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="Auto" maxLength={6} className="h-9 border-[#DAD5CA] uppercase" data-testid="dm-new-code" />
              </div>
            )}
            <Button onClick={add} disabled={busy || !newLabel.trim()}
              className="h-9 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-medium" data-testid="dm-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>

          {loading ? (
            <div className="p-6 text-sm text-[#8A857A]">Loading…</div>
          ) : (
            <div>
              {items.map((it, idx) => (
                <div key={it.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#F4F2ED] hover:bg-[#FBFAF7]" data-testid={`dm-item-${it.id}`}>
                  <div className="flex flex-col">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#A39D8F] hover:text-[#1A1A1A] disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="text-[#A39D8F] hover:text-[#1A1A1A] disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                  </div>

                  {editing?.id === it.id ? (
                    <>
                      <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                        className="h-8 flex-1 border-[#DAD5CA]" data-testid="dm-edit-label" autoFocus />
                      {hasCode && (
                        <Input value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                          maxLength={6} className="h-8 w-24 border-[#DAD5CA] uppercase" data-testid="dm-edit-code" />
                      )}
                      <Button size="sm" onClick={saveEdit} disabled={busy} className="h-8 bg-[#0E6B3F] hover:bg-[#0b5733] text-white" data-testid="dm-edit-save">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="h-8 border-[#DAD5CA]"><X className="w-3.5 h-3.5" /></Button>
                    </>
                  ) : (
                    <>
                      <span className={`flex-1 text-sm ${it.active ? "text-[#1A1A1A]" : "text-[#A39D8F] line-through"}`}>{it.label}</span>
                      {hasCode && it.code && <Badge variant="outline" className="border-[#DAD5CA] text-[#59554D] mono text-[11px]">{it.code}</Badge>}
                      {it.in_use > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#8A857A]" title="In use by documents">
                          <Lock className="w-3 h-3" /> {it.in_use} in use
                        </span>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] uppercase tracking-wide text-[#A39D8F]">{it.active ? "Active" : "Off"}</span>
                        <Switch checked={it.active} onCheckedChange={() => toggleActive(it)} data-testid={`dm-toggle-${it.id}`} />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ id: it.id, label: it.label, code: it.code })}
                        className="h-8 text-[#59554D] hover:text-[#1A1A1A]" data-testid={`dm-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(it)} disabled={it.in_use > 0}
                        className="h-8 text-[#9A3200] hover:text-[#7a2800] disabled:opacity-30" data-testid={`dm-del-${it.id}`}
                        title={it.in_use > 0 ? "In use — deactivate instead" : "Delete"}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              ))}
              {items.length === 0 && <div className="p-6 text-sm text-[#8A857A]">No options yet — add one above.</div>}
            </div>
          )}
        </CardContent>
      </Card>
      )}
      <AreaOwnersCard />
      <CsLeadCard />
    </div>
  );
}
